import {
  TOKENS_STORAGE_KEY,
  GENERATION_KEY,
  OIDC_USER_PREFIX,
  TOMBSTONE_KEY,
  readTokens,
  isUsableTokenSet,
  readGeneration,
  readTombstone,
  writeGeneration,
  clearGeneration,
  writeTombstone,
} from './auth_tokens';

export type AuthSyncMessage =
  | { kind: 'request' }
  | { kind: 'provide'; snapshot: Record<string, string>; generation: number; revision: number }
  | { kind: 'signout'; tombstone: number };

export interface AuthBus {
  post(msg: AuthSyncMessage): void;
  onMessage(cb: (msg: AuthSyncMessage) => void): () => void;
  close(): void;
}

export interface AuthSync {
  bootstrap(timeoutMs?: number): Promise<void>;
  emitLogin(): void;
  emitRefresh(): void;
  emitSignout(): void;
  onRemote(cb: (m: { kind: 'signout' }) => void): void;
}

const CHANNEL = 'voll.auth.sync';

function createBroadcastBus(name = CHANNEL): AuthBus | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  const ch = new BroadcastChannel(name);
  return {
    post: (msg) => {
      ch.postMessage(msg);
    },
    onMessage: (cb) => {
      const handler = (e: MessageEvent) => {
        cb(e.data as AuthSyncMessage);
      };
      ch.addEventListener('message', handler);
      return () => {
        ch.removeEventListener('message', handler);
      };
    },
    close: () => {
      ch.close();
    },
  };
}

function sessionKeys(storage: Storage): string[] {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const k = storage.key(i);
    if (k === null) continue;
    if (k === TOKENS_STORAGE_KEY || k === GENERATION_KEY || k.startsWith(OIDC_USER_PREFIX))
      keys.push(k);
  }
  return keys;
}

function getSnapshot(storage: Storage): Record<string, string> {
  const snap: Record<string, string> = {};
  for (const k of sessionKeys(storage)) {
    const v = storage.getItem(k);
    if (v !== null) snap[k] = v;
  }
  return snap;
}

function applySnapshot(storage: Storage, snapshot: Record<string, string>): void {
  for (const [k, v] of Object.entries(snapshot)) storage.setItem(k, v);
}

function clearSession(storage: Storage): void {
  for (const k of sessionKeys(storage)) storage.removeItem(k);
}

export function createAuthSync({
  bus,
  storage,
}: {
  bus: AuthBus | null;
  storage: Storage;
}): AuthSync {
  let remoteSignout: ((m: { kind: 'signout' }) => void) | null = null;
  let onProvideAdopted: (() => void) | null = null;
  let lastGeneration = readGeneration(storage);
  let lastRevision = 0;

  function accept(generation: number, revision: number): boolean {
    if (generation <= readTombstone(storage)) return false; // signed-out barrier
    if (generation !== lastGeneration) return generation > lastGeneration;
    return revision > lastRevision; // tie-break within a generation
  }

  function handle(msg: AuthSyncMessage): void {
    if (msg.kind === 'request') {
      if (isUsableTokenSet(readTokens(storage))) {
        bus?.post({
          kind: 'provide',
          snapshot: getSnapshot(storage),
          generation: readGeneration(storage),
          revision: Date.now(),
        });
      }
      return;
    }
    if (msg.kind === 'provide') {
      if (!accept(msg.generation, msg.revision)) return;
      applySnapshot(storage, msg.snapshot);
      lastGeneration = msg.generation;
      lastRevision = msg.revision;
      onProvideAdopted?.();
      return;
    }
    // signout
    writeTombstone(Math.max(readTombstone(storage), msg.tombstone), storage);
    clearSession(storage);
    lastGeneration = 0;
    lastRevision = 0;
    remoteSignout?.({ kind: 'signout' });
  }

  function bootstrap(timeoutMs = 300): Promise<void> {
    if (!bus) return Promise.resolve();
    bus.onMessage(handle);
    if (isUsableTokenSet(readTokens(storage)) || readTombstone(storage) > 0)
      return Promise.resolve();
    return new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (!done) {
          done = true;
          onProvideAdopted = null;
          resolve();
        }
      };
      onProvideAdopted = finish;
      bus.post({ kind: 'request' });
      setTimeout(finish, timeoutMs);
    });
  }

  return {
    bootstrap,
    emitLogin() {
      const generation = Date.now();
      writeGeneration(generation, storage);
      // clear our own tombstone: a fresh local login supersedes it
      storage.removeItem(TOMBSTONE_KEY);
      lastGeneration = generation;
      lastRevision = Date.now();
      bus?.post({
        kind: 'provide',
        snapshot: getSnapshot(storage),
        generation,
        revision: lastRevision,
      });
    },
    emitRefresh() {
      const generation = readGeneration(storage);
      lastRevision = Date.now();
      bus?.post({
        kind: 'provide',
        snapshot: getSnapshot(storage),
        generation,
        revision: lastRevision,
      });
    },
    emitSignout() {
      const tombstone = Date.now();
      writeTombstone(tombstone, storage); // apply locally (BroadcastChannel does not self-deliver)
      clearGeneration(storage);
      lastGeneration = 0;
      lastRevision = 0;
      bus?.post({ kind: 'signout', tombstone });
    },
    onRemote(cb) {
      remoteSignout = cb;
    },
  };
}

export const authSync: AuthSync = createAuthSync({
  bus: createBroadcastBus(),
  storage: globalThis.sessionStorage,
});
